import { computed, reactive, readonly } from 'vue';

import UserSetting from '@/models/UserSetting.js';
import toastService from '@/services/ToastService.js';
import {
    FILE_VIEWER_RENDERERS,
    FILE_VIEWER_USE_CASES,
    fileViewerDrawerSettingKey,
    getFileViewerCapability,
    resolveFileViewerFileName,
} from '@/utilities/fileViewerCapability.js';
import { resolveMediaDownloadHref } from '@/utilities/mediaDownloadHref.js';

/**
 * Module-scope File Viewer state (ADR-052). No Pinia/Vuex.
 * Call `reset()` from tests in beforeEach.
 */

const DRAWER_SIZES = Object.freeze({
    PARTIAL: 'partial',
    FULL: 'full',
    MINIMIZED: 'minimized',
});

const VIEWER_STATUS = Object.freeze({
    IDLE: 'idle',
    LOADING: 'loading',
    READY: 'ready',
    ERROR: 'error',
    UNSUPPORTED: 'unsupported',
});

const state = reactive({
    isOpen: false,
    drawerSize: DRAWER_SIZES.PARTIAL,
    status: VIEWER_STATUS.IDLE,
    file: null,
    context: null,
    sourceUrl: null,
    downloadHref: null,
    capability: null,
    errorCode: null,
});

/** @type {string|null} */
let blobUrl = null;

function revokeBlobUrl() {
    if (blobUrl) {
        URL.revokeObjectURL(blobUrl);
        blobUrl = null;
    }
}

/**
 * @param {unknown} file
 * @returns {boolean}
 */
function isBrowserFile(file) {
    return typeof File !== 'undefined' && file instanceof File;
}

/**
 * @param {object|null|undefined} context
 * @param {string} fileName
 */
function normalizeContext(context, fileName) {
    const useCase = FILE_VIEWER_USE_CASES.includes(context?.useCase) ? context.useCase : 'activity';

    return {
        useCase,
        fileName,
        courseName: context?.courseName ?? null,
        studentName: context?.studentName ?? null,
        activityName: context?.activityName ?? null,
    };
}

/**
 * @param {object|File} file
 * @returns {string|null}
 */
function resolvePreviewSource(file) {
    if (isBrowserFile(file)) {
        revokeBlobUrl();
        blobUrl = URL.createObjectURL(file);
        return blobUrl;
    }

    if (typeof file?.data === 'string' && (file.data.startsWith('blob:') || file.data.startsWith('data:'))) {
        return file.data;
    }

    return resolveMediaDownloadHref(file);
}

/**
 * @param {'activity'|'grader'|'submission'} useCase
 */
async function loadDrawerSize(useCase) {
    try {
        const setting = UserSetting.findByKey(fileViewerDrawerSettingKey(useCase));
        const saved = setting?.value;

        if (saved === DRAWER_SIZES.FULL || saved === DRAWER_SIZES.PARTIAL) {
            state.drawerSize = saved;
            return;
        }
    } catch {
        toastService.open({
            message: 'Failed to load File Viewer drawer size',
            type: 'is-warning',
        });
    }

    state.drawerSize = DRAWER_SIZES.PARTIAL;
}

/**
 * Persist partial/full only. Minimized must never overwrite the saved value.
 * @param {'partial'|'full'|'minimized'} size
 */
async function setDrawerSize(size) {
    if (size === DRAWER_SIZES.MINIMIZED) {
        state.drawerSize = DRAWER_SIZES.MINIMIZED;
        return;
    }

    if (size !== DRAWER_SIZES.PARTIAL && size !== DRAWER_SIZES.FULL) {
        return;
    }

    state.drawerSize = size;

    const useCase = state.context?.useCase;
    if (!useCase) {
        return;
    }

    try {
        const setting = UserSetting.findByKey(fileViewerDrawerSettingKey(useCase));
        await setting.set(size);
    } catch {
        // Persistence failures should not break preview.
        toastService.open({
            message: 'Failed to save File Viewer drawer size',
            type: 'is-warning',
        });
    }
}

/**
 * @param {{ file: object|File, context?: object }} options
 */
async function open({ file, context } = {}) {
    if (!file) {
        return;
    }

    const wasOpen = state.isOpen;
    const previousSourceUrl = state.sourceUrl;
    const previousStatus = state.status;
    const fileName = resolveFileViewerFileName(file);
    const normalizedContext = normalizeContext(context, fileName);
    const capability = getFileViewerCapability(file);

    revokeBlobUrl();

    state.file = file;
    state.context = normalizedContext;
    state.capability = capability;
    state.errorCode = null;
    state.downloadHref = resolveMediaDownloadHref(file);

    if (!wasOpen) {
        await loadDrawerSize(normalizedContext.useCase);
    }

    state.isOpen = true;

    if (!capability.supported) {
        state.sourceUrl = null;
        state.status = VIEWER_STATUS.UNSUPPORTED;
        return;
    }

    try {
        const sourceUrl = resolvePreviewSource(file);

        if (isBrowserFile(file) && sourceUrl) {
            state.downloadHref = sourceUrl;
        }

        state.sourceUrl = sourceUrl;

        if (!sourceUrl && capability.renderer !== FILE_VIEWER_RENDERERS.DOCUMENT) {
            state.status = VIEWER_STATUS.ERROR;
            state.errorCode = 'missing_source';
            return;
        }

        if (!sourceUrl && capability.renderer === FILE_VIEWER_RENDERERS.DOCUMENT) {
            state.status = VIEWER_STATUS.READY;
            return;
        }

        const sameReadySource =
            wasOpen &&
            previousStatus === VIEWER_STATUS.READY &&
            previousSourceUrl != null &&
            sourceUrl != null &&
            previousSourceUrl === sourceUrl;

        if (sameReadySource) {
            state.status = VIEWER_STATUS.READY;
            return;
        }

        state.status = VIEWER_STATUS.LOADING;
        // Stay loading until the mounted renderer emits ready.
    } catch {
        state.sourceUrl = null;
        state.status = VIEWER_STATUS.ERROR;
        state.errorCode = 'open_failed';
    }
}

function close() {
    state.isOpen = false;
    revokeBlobUrl();
    state.file = null;
    state.context = null;
    state.sourceUrl = null;
    state.downloadHref = null;
    state.capability = null;
    state.status = VIEWER_STATUS.IDLE;
    state.errorCode = null;

    // Keep last non-minimized size in memory for same-session reopen before settings load.
    if (state.drawerSize === DRAWER_SIZES.MINIMIZED) {
        state.drawerSize = DRAWER_SIZES.PARTIAL;
    }
}

function markReady() {
    if (!state.isOpen || state.status === VIEWER_STATUS.UNSUPPORTED || state.status === VIEWER_STATUS.ERROR) {
        return;
    }

    state.status = VIEWER_STATUS.READY;
}

function markError(errorCode = 'render_failed') {
    state.status = VIEWER_STATUS.ERROR;
    state.errorCode = errorCode;
}

function reset() {
    close();
    state.drawerSize = DRAWER_SIZES.PARTIAL;
    state.status = VIEWER_STATUS.IDLE;
    state.errorCode = null;
}

/**
 * Shared File Viewer API.
 */
export function useFileViewer() {
    const isOpen = computed(() => state.isOpen);
    const drawerSize = computed(() => state.drawerSize);
    const status = computed(() => state.status);
    const file = computed(() => state.file);
    const context = computed(() => state.context);
    const sourceUrl = computed(() => state.sourceUrl);
    const downloadHref = computed(() => state.downloadHref);
    const capability = computed(() => state.capability);
    const errorCode = computed(() => state.errorCode);
    const title = computed(() => {
        const name = state.context?.fileName || resolveFileViewerFileName(state.file);
        return `File Viewer: ${name}`;
    });
    const isMaximized = computed(() => state.drawerSize === DRAWER_SIZES.FULL);

    return {
        state: readonly(state),
        isOpen,
        drawerSize,
        status,
        file,
        context,
        sourceUrl,
        downloadHref,
        capability,
        errorCode,
        title,
        isMaximized,
        open,
        close,
        setDrawerSize,
        markReady,
        markError,
        reset,
        DRAWER_SIZES,
        VIEWER_STATUS,
    };
}

export { DRAWER_SIZES, VIEWER_STATUS };
